import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { RiDeleteBack2Line } from '@remixicon/react';
import {
  ArrowDown,
  ArrowUp,
  CheckIcon,
  CornerDownLeft,
  PlusIcon,
  XIcon,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TOOL_LIST, TOOL_LIST_BY_PROVIDER } from './constants';

interface ToolsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTools: string[];
  onToolsChange: (tools: string[]) => void;
}

export function ToolsDialog({
  isOpen,
  onClose,
  selectedTools,
  onToolsChange,
}: ToolsDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredToolsByProvider = useMemo(() => {
    if (!searchTerm) return TOOL_LIST_BY_PROVIDER;

    const filtered: Record<string, typeof TOOL_LIST> = {};

    for (const [providerName, tools] of Object.entries(TOOL_LIST_BY_PROVIDER)) {
      const matchingTools = tools.filter(
        (tool) =>
          tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tool.description.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (matchingTools.length > 0) {
        filtered[providerName] = matchingTools;
      }
    }

    return filtered;
  }, [searchTerm]);

  const handleToggleTool = useCallback(
    (toolValue: string, event?: React.MouseEvent) => {
      event?.stopPropagation();

      const updatedTools = selectedTools.includes(toolValue)
        ? selectedTools.filter((t) => t !== toolValue)
        : [...selectedTools, toolValue];

      onToolsChange(updatedTools);
    },
    [selectedTools, onToolsChange]
  );

  const handleRemoveSelectedTool = useCallback(
    (toolValue: string) => {
      onToolsChange(selectedTools.filter((t) => t !== toolValue));
    },
    [selectedTools, onToolsChange]
  );

  // Handle keyboard events for backspace removal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      // Handle backspace to remove tools
      if (event.key === 'Backspace') {
        const target = event.target as HTMLElement;

        // Skip if we're typing in the search input and it has content
        if (target.matches('input[cmdk-input]') && searchTerm) {
          return;
        }

        // Find the currently highlighted/selected command item
        const selectedItem = document.querySelector(
          '[cmdk-item][data-selected="true"]'
        );

        if (selectedItem) {
          const toolValue = selectedItem.getAttribute('data-value');
          if (toolValue && selectedTools.includes(toolValue)) {
            event.preventDefault();
            event.stopPropagation();
            console.log('Removing tool via backspace:', toolValue); // Debug log
            handleRemoveSelectedTool(toolValue);
          }
        }
      }
    };

    // Use capture phase to ensure we catch the event before cmdk processes it
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () =>
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [isOpen, searchTerm, selectedTools, handleRemoveSelectedTool]);

  const getSelectedToolName = (toolValue: string) => {
    const tool = TOOL_LIST.find((t) => t.value === toolValue);
    return tool?.name || toolValue;
  };

  const truncateDescription = (description: string, maxLength = 120) => {
    if (description.length <= maxLength) return description;
    return `${description.substring(0, maxLength)}...`;
  };

  const handleToolSelect = useCallback(
    (toolValue: string) => {
      const isSelected = selectedTools.includes(toolValue);

      if (isSelected) {
        // If already selected, remove it
        handleRemoveSelectedTool(toolValue);
      } else {
        // If not selected, add it
        handleToggleTool(toolValue);
      }
    },
    [selectedTools, handleRemoveSelectedTool, handleToggleTool]
  );

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={onClose}
      title="Manage Agent Tools"
      description="Search and manage available tools for your agent. Use ↑↓ to navigate, Enter to add/remove, Backspace to remove selected tools."
    >
      <CommandInput
        placeholder="Search tools..."
        value={searchTerm}
        onValueChange={setSearchTerm}
      />

      <div className="border-b p-4">
        {/* Selected Tools */}
        {selectedTools.length > 0 ? (
          <>
            <div className="mb-2 font-medium text-sm">
              Selected Tools ({selectedTools.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedTools.map((toolValue) => (
                <Badge
                  key={toolValue}
                  variant="secondary"
                  className="flex items-center gap-1 pr-1"
                >
                  {getSelectedToolName(toolValue)}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveSelectedTool(toolValue)}
                    className="h-4 w-4 p-0 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <XIcon className="size-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          </>
        ) : (
          <div className="text-muted-foreground text-sm">
            No tools selected, add some to get started
          </div>
        )}
      </div>

      <CommandList className="max-h-[400px]">
        <CommandEmpty>No tools found matching your search.</CommandEmpty>

        {Object.entries(filteredToolsByProvider).map(
          ([providerName, tools]) => (
            <CommandGroup key={providerName} heading={providerName}>
              {tools.map((tool) => {
                const isSelected = selectedTools.includes(tool.value);

                return (
                  <CommandItem
                    key={tool.value}
                    value={tool.value}
                    onSelect={() => handleToolSelect(tool.value)}
                    className="flex cursor-pointer flex-col items-start gap-2 p-4"
                  >
                    <div className="flex w-full items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{tool.name}</p>
                          {isSelected && (
                            <CheckIcon className="size-4 text-green-600" />
                          )}
                        </div>
                        <p className="mt-1 line-clamp-2 text-muted-foreground text-xs">
                          {truncateDescription(tool.description)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="icon"
                          variant={'ghost'}
                          onClick={(e) => handleToggleTool(tool.value, e)}
                          className="ml-2 shrink-0"
                        >
                          {isSelected ? (
                            <XIcon className="size-4 text-red-500" />
                          ) : (
                            <PlusIcon className="size-4 text-emerald-500" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )
        )}
      </CommandList>

      {/* Footer */}
      <div className="flex justify-between gap-2 border-t p-2 px-4">
        <div className="hidden gap-3 text-xs md:flex">
          <div className="flex items-center gap-2">
            <ArrowUp className="size-4" />
            <ArrowDown className="size-4" />
            <span className="text-paragraph-xs text-text-sub-600">
              Navigate
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CornerDownLeft className="size-4" />
            <span className="text-paragraph-xs text-text-sub-600">Add</span>
          </div>
          {searchTerm === '' && (
            <div className="flex items-center gap-2">
              <RiDeleteBack2Line className="size-4" />
              <span className="text-paragraph-xs text-text-sub-600">
                Remove
              </span>
            </div>
          )}
        </div>
      </div>
    </CommandDialog>
  );
}
